/**
 * Compact period picker used by the financial-report routes.
 *
 * Presets: month / quarter / fiscal year + statement quick-selects.
 * Custom mode reveals two date inputs.
 */

import { useState } from "react";
import { Calendar } from "lucide-react";

import {
  Input,
  cn,
} from "@bizop/ui-kit";

import {
  getCurrentMonthWindow,
  getCurrentQuarterWindow,
  getFiscalYearWindow,
  MOCK_NOW,
  type PeriodKey,
} from "../../lib/glMock";

export type PeriodWindow = {
  fromDate: number;
  toDate: number;
  key: PeriodKey;
};

export function defaultPeriodWindow(): PeriodWindow {
  const w = getCurrentMonthWindow(MOCK_NOW);
  return { ...w, key: "month" };
}

const PRESETS: Array<{ key: PeriodKey; label: string }> = [
  { key: "month", label: "This month" },
  { key: "quarter", label: "This quarter" },
  { key: "fiscal_year", label: "Fiscal year" },
  { key: "custom", label: "Custom" },
];

/* ─────────────────────────────────────────────────────────────
 * Quick-select helpers for statement pages
 * ───────────────────────────────────────────────────────────── */

export type QuickRange =
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "this_year"
  | "custom";

const QUICK_RANGES: Array<{ key: QuickRange; label: string }> = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "last_3_months", label: "Last 3 Months" },
  { key: "last_6_months", label: "Last 6 Months" },
  { key: "this_year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

/**
 * Compute from/to timestamps for a given quick-range key.
 * Returns null for "custom" (caller handles that mode separately).
 */
export function quickRangeWindow(
  key: QuickRange,
  asOf: number = MOCK_NOW,
): { fromDate: number; toDate: number } | null {
  const d = new Date(asOf);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();

  switch (key) {
    case "this_month":
      return {
        fromDate: Date.UTC(y, m, 1),
        toDate: asOf,
      };
    case "last_month":
      return {
        fromDate: Date.UTC(y, m - 1, 1),
        toDate: Date.UTC(y, m, 0, 23, 59, 59, 999),
      };
    case "last_3_months": {
      return {
        fromDate: Date.UTC(y, m - 2, 1),
        toDate: asOf,
      };
    }
    case "last_6_months": {
      return {
        fromDate: Date.UTC(y, m - 5, 1),
        toDate: asOf,
      };
    }
    case "this_year":
      return {
        fromDate: Date.UTC(y, 0, 1),
        toDate: asOf,
      };
    case "custom":
      return null;
  }
}

/* ─────────────────────────────────────────────────────────────
 * QuickRangeBar — row of quick-select buttons for statements
 * ───────────────────────────────────────────────────────────── */

export function QuickRangeBar({
  value,
  onChange,
}: {
  value: QuickRange;
  onChange: (key: QuickRange, window: PeriodWindow | null) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mr-1">
        Quick range
      </span>
      {QUICK_RANGES.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => {
            const w = quickRangeWindow(r.key);
            onChange(
              r.key,
              w ? { ...w, key: r.key === "custom" ? "custom" : "month" } : null,
            );
          }}
          className={cn(
            "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
            value === r.key
              ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
              : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Original PeriodSelector (unchanged API)
 * ───────────────────────────────────────────────────────────── */

export function PeriodSelector({
  value,
  onChange,
}: {
  value: PeriodWindow;
  onChange: (next: PeriodWindow) => void;
}) {
  const [from, setFrom] = useState<string>(
    new Date(value.fromDate).toISOString().slice(0, 10),
  );
  const [to, setTo] = useState<string>(
    new Date(value.toDate).toISOString().slice(0, 10),
  );

  function pick(key: PeriodKey) {
    if (key === "month") {
      const w = getCurrentMonthWindow(MOCK_NOW);
      onChange({ ...w, key });
      setFrom(new Date(w.fromDate).toISOString().slice(0, 10));
      setTo(new Date(w.toDate).toISOString().slice(0, 10));
    } else if (key === "quarter") {
      const w = getCurrentQuarterWindow(MOCK_NOW);
      onChange({ ...w, key });
      setFrom(new Date(w.fromDate).toISOString().slice(0, 10));
      setTo(new Date(w.toDate).toISOString().slice(0, 10));
    } else if (key === "fiscal_year") {
      const w = getFiscalYearWindow(MOCK_NOW);
      onChange({ ...w, key });
      setFrom(new Date(w.fromDate).toISOString().slice(0, 10));
      setTo(new Date(w.toDate).toISOString().slice(0, 10));
    } else {
      onChange({ ...value, key });
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
      {PRESETS.map((p) => (
        <button
          key={p.key}
          type="button"
          onClick={() => pick(p.key)}
          className={cn(
            "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
            value.key === p.key
              ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
              : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
          )}
        >
          {p.label}
        </button>
      ))}
      {value.key === "custom" ? (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={from}
            className="h-7 w-36 text-[12px]"
            onChange={(e) => {
              setFrom(e.target.value);
              onChange({
                ...value,
                fromDate: new Date(e.target.value).getTime(),
              });
            }}
          />
          <span className="text-(--text-tertiary) text-[12px]">{"→"}</span>
          <Input
            type="date"
            value={to}
            className="h-7 w-36 text-[12px]"
            onChange={(e) => {
              setTo(e.target.value);
              onChange({
                ...value,
                toDate: new Date(e.target.value).getTime(),
              });
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
